import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Copy, RefreshCw, Send, QrCode, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const ADMIN_ROLES = ["owner", "admin"];
const WRITE_ROLES = ["owner", "admin", "project_manager", "superintendent"];

export default function ProjectEmailCard({ projectId, projectName }) {
  const { user } = useAuth();
  const canRegen = ADMIN_ROLES.includes(user?.role);
  const canTest = WRITE_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [working, setWorking] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/upload-email`);
      setData(r.data);
    } catch (e) {
      // silent — card just won't render the address
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const [qrSrc, setQrSrc] = useState(null);
  useEffect(() => {
    if (!showQr) {
      if (qrSrc) { URL.revokeObjectURL(qrSrc); setQrSrc(null); }
      return;
    }
    api.get(`/projects/${projectId}/upload-email/qr`, { responseType: "blob" })
      .then((r) => setQrSrc(URL.createObjectURL(r.data)))
      .catch(() => toast.error("QR unavailable"));
  /* eslint-disable-next-line */
  }, [showQr, projectId]);

  if (!data) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.address);
      toast.success("Address copied");
    } catch { toast.error("Copy failed"); }
  };

  const regen = async () => {
    if (!window.confirm("Regenerate the project upload email? The previous address will stop accepting mail.")) return;
    setWorking(true);
    try {
      const r = await api.post(`/projects/${projectId}/upload-email/regenerate`);
      setData({ ...data, ...r.data });
      toast.success("New address provisioned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally { setWorking(false); }
  };

  const sendTest = async () => {
    setWorking(true);
    try {
      const r = await api.post(`/intake/email/test`, { project_id: projectId });
      if (r.data?.sent) toast.success(`Test sent to ${r.data.to}`);
      else toast.info("Email provider not configured — verified address only");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setWorking(false); }
  };

  return (
    <div className="fop-surface-2 rounded-lg p-4 border border-[var(--fop-border)]" data-testid="project-email-card">
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-2 text-[var(--fop-text-2)]">
          <Mail size={14} className="text-[var(--fop-primary)]" />
          <span className="font-mono text-[10px] uppercase tracking-wider">Project Upload Email</span>
        </div>
        <span className="text-[10px] font-mono text-[var(--fop-text-4)]">Forward drawings, specs, permits</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <code className="px-2 py-1.5 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs font-mono text-[var(--fop-text)] break-all flex-1 min-w-0" data-testid="upload-email-address">
          {data.address}
        </code>
        <button onClick={copy} className="btn btn-ghost text-[11px] inline-flex items-center gap-1" data-testid="copy-upload-email">
          <Copy size={11} /> Copy
        </button>
        <button onClick={() => setShowQr(true)} className="btn btn-ghost text-[11px] inline-flex items-center gap-1" data-testid="show-qr-upload-email">
          <QrCode size={11} /> QR
        </button>
        {canTest && (
          <button onClick={sendTest} disabled={working} className="btn btn-ghost text-[11px] inline-flex items-center gap-1" data-testid="test-upload-email">
            {working ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Test
          </button>
        )}
        {canRegen && (
          <button onClick={regen} disabled={working} className="btn btn-ghost text-[11px] inline-flex items-center gap-1 text-amber-400" data-testid="regenerate-upload-email">
            {working ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Regenerate
          </button>
        )}
      </div>
      {showQr && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowQr(false)}>
          <div className="bg-white rounded-lg p-4 max-w-xs border border-[var(--fop-border-hi)]" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">Scan to email</span>
              <button onClick={() => setShowQr(false)} className="text-zinc-500 hover:text-zinc-900"><X size={14} /></button>
            </div>
            <img
              src={qrSrc || ""}
              alt={`QR for ${data.address}`}
              className="w-full h-auto"
              data-testid="qr-image"
              style={{ display: qrSrc ? "block" : "none" }}
            />
            {!qrSrc && (
              <div className="py-10 text-center text-[10px] font-mono text-zinc-500 inline-flex items-center justify-center gap-2 w-full">
                <Loader2 size={12} className="animate-spin" /> Generating QR…
              </div>
            )}
            <div className="mt-2 text-[10px] font-mono text-zinc-600 break-all text-center">{data.address}</div>
          </div>
        </div>
      )}
    </div>
  );
}
